import {
  useId,
  useState,
  type ComponentPropsWithRef,
  type ComponentType,
  type ReactNode,
} from 'react';
import { IconCircleAlert, IconEye, IconEyeOff, IconSearch, type IconProps } from '../icons';
import { useTranslate } from '../i18n/react';
import { IconButton, cx } from './Button';

/**
 * A labelled field.
 *
 * <p>The label sits above the control and stays there. A placeholder standing in for a label
 * disappears the moment someone types, which is exactly when they look back up to check
 * which box they are in.</p>
 *
 * <p>An error is wired through `aria-describedby` and `aria-invalid`, and says what to do
 * next — "Geçerli bir e-posta adresi girin", not "Invalid".</p>
 */
export function Field({
  label,
  hint,
  error,
  required,
  children,
}: {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  required?: boolean;
  /** Receives the id to put on the control and the ids describing it. */
  children: (ids: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
}) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ') || undefined;

  return (
    <div className="nv-field">
      {label ? (
        <label className="nv-label" htmlFor={id}>
          {label}
          {required ? <span aria-hidden="true" className="nv-required">*</span> : null}
        </label>
      ) : null}

      {children({ id, describedBy, invalid: Boolean(error) })}

      {hint ? <span className="nv-hint" id={hintId}>{hint}</span> : null}
      {error ? (
        <span className="nv-error-text" id={errorId}>
          <IconCircleAlert size={13} />
          {error}
        </span>
      ) : null}
    </div>
  );
}

export type TextInputProps = ComponentPropsWithRef<'input'> & {
  /** Drawn inside the field, on the leading edge. */
  icon?: ComponentType<IconProps>;
  large?: boolean;
  /** Rendered on the trailing edge — a show/hide toggle, a unit, a shortcut hint. */
  trailing?: ReactNode;
};

export function TextInput({ icon: Icon, large, trailing, className, type = 'text', ...rest }: TextInputProps) {
  // A date box takes a six-digit year unless told otherwise: digits typed into the year part
  // made "122026-12-01", and Save stayed open (field test, 3 November). A four-digit max
  // keeps the year to four.
  const max = type === 'date' && rest.max === undefined ? '9999-12-31' : rest.max;
  return (
    <span className="nv-input-wrap">
      {Icon ? <Icon size={16} /> : null}
      <input type={type} className={cx('nv-input', large && 'nv-input--lg', className)} {...rest} max={max} />
      {trailing}
    </span>
  );
}

/** Email, name, anything typed once. */
export function Select({ className, children, ...rest }: ComponentPropsWithRef<'select'>) {
  return (
    <select className={cx('nv-select', className)} {...rest}>
      {children}
    </select>
  );
}

export function Textarea({ className, ...rest }: ComponentPropsWithRef<'textarea'>) {
  return <textarea className={cx('nv-input', 'nv-textarea', className)} {...rest} />;
}

/** A password field whose contents can be checked before it is sent. */
export function PasswordInput({ large, ...rest }: Omit<TextInputProps, 'type' | 'trailing'>) {
  const t = useTranslate();
  const [shown, setShown] = useState(false);

  return (
    <TextInput
      {...rest}
      large={large}
      type={shown ? 'text' : 'password'}
      trailing={
        <IconButton
          className="nv-input-action"
          label={shown ? t('login.hidePassword') : t('login.showPassword')}
          icon={shown ? IconEyeOff : IconEye}
          onClick={() => setShown((s) => !s)}
        />
      }
    />
  );
}

/**
 * The toolbar search box.
 *
 * <p>`/` focuses it, which is the one shortcut worth having on a screen whose job is to find
 * a row in several hundred.</p>
 */
export function SearchInput({
  value,
  onValueChange,
  placeholder,
  shortcut = true,
}: {
  value: string;
  onValueChange: (next: string) => void;
  placeholder: string;
  shortcut?: boolean;
}) {
  const t = useTranslate();

  return (
    <span className="nv-input-wrap nv-toolbar__search">
      <IconSearch size={16} />
      <input
        type="search"
        className="nv-input"
        aria-label={t('common.search')}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        data-search-box={shortcut ? 'true' : undefined}
      />
      {shortcut && value === '' ? <kbd className="nv-kbd" aria-hidden="true">/</kbd> : null}
    </span>
  );
}

/** A checkbox that reads as a sentence rather than a lone tick. */
export function Toggle({
  checked,
  onCheckedChange,
  label,
  disabled,
}: {
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
  label: ReactNode;
  disabled?: boolean;
}) {
  return (
    <label className={cx('nv-toggle', disabled && 'is-disabled')}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onCheckedChange(e.target.checked)}
      />
      <span>{label}</span>
    </label>
  );
}

/** What went wrong with the whole form, above it, once. */
export function Alert({ children, tone = 'danger' }: { children: ReactNode; tone?: 'danger' | 'warning' | 'info' }) {
  return (
    <div className={cx('nv-alert', tone !== 'danger' && `nv-alert--${tone}`)} role={tone === 'danger' ? 'alert' : 'status'}>
      <IconCircleAlert size={16} />
      <span>{children}</span>
    </div>
  );
}
